import { ProductNameEnum } from "@nlc/shared-types";
import { useNavigate } from "@tanstack/react-router";
import { Calendar, Undo2 } from "lucide-react";

import heroImage from "@/assets/pricing-header-image.png";
import { Button } from "@/components/button";
import { useGoogleTagManager } from "@/features/auth/subscription/hooks/useGoogleTagManager.ts";
import { useOptionalUser } from "@/hooks/useOptionalUser";
import { useProducts } from "@/hooks/useProducts";

const PRICING_PLANS = [
  {
    credits: 3,
    features: ["Unlimited Brands", "Standard Support"],
    featuresDescription: "",
    name: ProductNameEnum.DIGITAL_ADS,
    price: 500,
    type: "Get all your social media graphic & video ads made all day, every day, like a well-oiled machine.",
  },
  {
    credits: 2,
    features: [
      "Unlimited Design Request",
      "Unlimited Brands",
      "Standard Support",
    ],
    featuresDescription: "All of Digital Ads, plus:",
    name: ProductNameEnum.ECOM,
    price: 600,
    type: "Get all the graphic & video creatives you need to run a successful e-commerce brand.",
  },
  {
    credits: 4,
    features: [
      "Unlimited Design Request",
      "Unlimited Brands",
      "Standard Support",
    ],
    featuresDescription: "All of Ecom, plus:",
    name: ProductNameEnum.MARKETING,
    price: 700,
    type: "Scale your agency with stunning designs for every single one of your brands.",
  },
  {
    credits: 6,
    features: ["Unlimited Design Request", "Unlimited Brands", "Pro Support"],
    featuresDescription: "All of Marketing, plus:",
    name: ProductNameEnum.FULL_STACK,
    price: 1300,
    type: "Build and launch websites or take advantage of all the other design types we have to offer.",
  },
];

interface PricingCardProps {
  isPopular?: boolean;
  plan: (typeof PRICING_PLANS)[0];
  stripeMonthlyProductId?: null | string;
  userEmail?: string;
}

function PricingCard({
  isPopular,
  plan,
  stripeMonthlyProductId,
  userEmail,
}: PricingCardProps) {
  useGoogleTagManager("GTM-5X67HTS");

  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (stripeMonthlyProductId) {
      const searchParams: {
        price: string;
        product_id: string;
        receipt_email?: string;
      } = {
        price: Number(plan.price).toLocaleString("en-US"),
        product_id: stripeMonthlyProductId,
      };

      if (userEmail) {
        searchParams.receipt_email = userEmail;
      }

      navigate({
        search: searchParams,
        to: "/subscription",
      });
    } else {
      navigate({ to: "/subscription" });
    }
  };

  return (
    <div
      className={`bg-white rounded-2xl border ${isPopular ? "border-purple-600 shadow-lg" : "border-border-secondary"} p-4 relative`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-purple-600 text-white px-4 py-1 rounded-full text-sm font-medium">
            Most Popular
          </span>
        </div>
      )}

      <div className="mb-3">
        <h3 className="text-lg font-bold text-text-primary mb-1">
          {plan.name}
        </h3>
      </div>

      <div className="mb-2 flex items-start justify-center relative">
        <p className="text-[34px] font-black absolute top-0 left-3">$</p>
        <div className="mb-8">
          <p className="text-[60px] leading-tight text-black font-bold text-center mb-2">
            {Number(plan.price).toLocaleString("en-US")}
          </p>
          <p className="text-[20px] leading-none text-text-secondary text-center">
            Billed monthly
          </p>
        </div>
      </div>

      <Button
        className="w-full mb-6"
        onPress={handleGetStarted}
        variant="primary"
      >
        Get Started!
      </Button>

      <div className="space-y-4">
        <div className="flex flex-col gap-2 min-h-[50px]">
          <h4 className="font-semibold text-text-primary">
            Included design capabilities:
          </h4>
          <p className="text-sm text-text-secondary">
            {plan.featuresDescription}
          </p>
        </div>
        <ul className="space-y-3">
          {plan.features.map((feature, index) => (
            <li
              className="flex items-center text-sm text-text-secondary"
              key={index}
            >
              <svg
                className="w-5 h-5 text-[#6938EF] mr-2 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  clipRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  fillRule="evenodd"
                />
              </svg>
              {feature}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function PricingPage() {
  const navigate = useNavigate();
  const { data: products } = useProducts();
  const { data: userData } = useOptionalUser();

  const getStripeIdForPlan = (planName: string) => {
    const product = products?.find((p) => p.title === planName);

    return product?.stripeMonthlyProductId || null;
  };

  return (
    <div className="min-h-screen bg-bg-primary">
      <div className="max-w-[1120px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-2">
          <button
            className="flex items-center text-[#5925DC] hover:text-brand-primary-alt font-[600] cursor-pointer"
            onClick={() => navigate({ to: "/" })}
          >
            <Undo2 className="w-5 h-5 mr-2" />
            Return to NLC Platform
          </button>
        </div>
        <div className="bg-white rounded-3xl overflow-hidden shadow-lg">
          {/* Hero Section */}
          <div
            className="text-white px-6 py-10"
            style={{
              background: "linear-gradient(45deg, #42307D 0%, #7F56D9 100%)",
            }}
          >
            <div className="items-center flex flex-row justify-between">
              <div>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-[1.2]">
                  Flexible design plans,
                  <br /> built for scale
                </h1>
                <p className="text-xl text-purple-100 mb-8">
                  Choose your design capabilities, design volume, and experience
                  a <br />
                  seamless and scalable design partnership.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button
                    className="bg-white text-[#5925DC] hover:bg-gray-50"
                    onPress={() => navigate({ to: "/kickstarter-call" })}
                    variant="secondary"
                  >
                    <Calendar className="w-4 h-4 mr-1" />
                    Book a call
                  </Button>
                </div>
              </div>

              <div>
                <img
                  alt="Hero Image"
                  className="w-full h-full object-cover"
                  src={heroImage}
                />
              </div>
            </div>
          </div>

          {/* Pricing Plans Section */}
          <div className="px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
              {PRICING_PLANS.map((plan) => (
                <PricingCard
                  isPopular={false}
                  key={plan.name}
                  plan={plan}
                  stripeMonthlyProductId={getStripeIdForPlan(plan.name)}
                  userEmail={userData?.user?.email}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
